/**
 * Main-thread handle on the render engine: a Web Worker when available,
 * otherwise (tests, very old browsers) the engine runs inline.
 */
import { RenderEngine } from './engine.ts';
import type { FromWorker, ToWorker } from './protocol.ts';
import type { RenderMode } from './mercator.ts';
import type { SummaryLevel } from './raster.ts';

export class RendererClient {
  private worker: Worker | null = null;
  private inline: RenderEngine | null = null;
  private pending = new Map<number, { resolve: (png: ArrayBuffer) => void; reject: (e: Error) => void }>();
  private nextId = 1;
  private disposed = false;

  constructor(useWorker = typeof Worker !== 'undefined') {
    if (useWorker) {
      try {
        this.worker = new Worker(new URL('./worker.ts', import.meta.url), { type: 'module' });
        this.worker.onmessage = (ev: MessageEvent<FromWorker>) => this.onMessage(ev.data);
        this.worker.onerror = (ev) => {
          console.error('render worker failed; falling back to inline rendering', ev.message);
          this.failWorker();
        };
      } catch {
        this.worker = null;
      }
    }
    if (!this.worker) this.inline = new RenderEngine();
  }

  /** Switch to inline rendering and fail pending renders so callers retry. */
  private failWorker(): void {
    const w = this.worker;
    this.worker = null;
    w?.terminate();
    this.inline ??= new RenderEngine();
    for (const [id, p] of this.pending) {
      this.pending.delete(id);
      p.reject(new Error('render worker failed'));
    }
  }

  private onMessage(msg: FromWorker): void {
    const p = this.pending.get(msg.id);
    if (!p) return;
    this.pending.delete(msg.id);
    if (msg.type === 'rendered') p.resolve(msg.png);
    else p.reject(new Error(msg.message));
  }

  private post(msg: ToWorker, transfer: Transferable[] = []): void {
    if (this.worker) this.worker.postMessage(msg, transfer);
    else void this.inline!.handle(msg);
  }

  setTile(key: number, pixels: Uint8Array | null): void {
    // Copy so the caller keeps its array.
    this.post({ type: 'tile', key, pixels: pixels ? new Uint8Array(pixels) : null });
  }

  applyDiff(key: number, entries: Array<{ idx: number; colour: number }>): void {
    this.post({ type: 'diff', key, entries });
  }

  setSummary(level: SummaryLevel, entries: Array<[number, number]>): void {
    this.post({ type: 'summary', level, entries });
  }

  clearTiles(): void {
    this.post({ type: 'clearTiles' });
  }

  render(z: number, x: number, y: number, size: number, mode: RenderMode): Promise<ArrayBuffer> {
    if (this.disposed) return Promise.reject(new Error('renderer disposed'));
    const id = this.nextId++;
    const msg: ToWorker = { type: 'render', id, z, x, y, size, mode };
    if (this.inline) {
      return this.inline.handle(msg).then((res) => {
        if (!res) throw new Error('no render result');
        if (res.type === 'error') throw new Error(res.message);
        return res.png;
      });
    }
    return new Promise((resolve, reject) => {
      this.pending.set(id, { resolve, reject });
      this.worker!.postMessage(msg);
    });
  }

  terminate(): void {
    this.disposed = true;
    this.worker?.terminate();
    this.worker = null;
    for (const [id, p] of this.pending) {
      this.pending.delete(id);
      p.reject(new Error('renderer disposed'));
    }
  }
}
